import { useState } from 'react';
import { Send, Trash2, Pencil, Check, X } from 'lucide-react';
import { inventoryApi } from '../../services/inventoryApi';
import type { InventoryComment } from '../../types/inventory';

interface InventoryCommentsProps {
    itemId: string;
    comments: InventoryComment[];
    canEdit: boolean;
    onCommentAdded: () => void;
    /**
     * When set, new comments created from this view are tagged with this lotCode (lot-specific).
     * When null/undefined, new comments are product-level (LotCode = NULL in DB).
     * Existing comments always show their own LotCode badge regardless of this prop.
     */
    lotCode?: string | null;
}

const TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
    note: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Note' },
    action: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Action' },
    decision: { bg: 'bg-green-100', text: 'text-green-700', label: 'Decision' },
};

function CommentItem({ comment, itemId, canEdit, onChanged }: {
    comment: InventoryComment;
    itemId: string;
    canEdit: boolean;
    onChanged: () => void;
}) {
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState(comment.CommentText);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!editText.trim() || saving) return;
        setSaving(true);
        try {
            await inventoryApi.updateComment(itemId, comment.Id, editText.trim());
            setEditing(false);
            onChanged();
        } catch (err) {
            console.error('Failed to update comment:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await inventoryApi.deleteComment(itemId, comment.Id);
            onChanged();
        } catch (err) {
            console.error('Failed to delete comment:', err);
        }
    };

    const handleCancel = () => {
        setEditText(comment.CommentText);
        setEditing(false);
    };

    const typeStyle = TYPE_STYLES[comment.CommentType] || TYPE_STYLES.note;

    return (
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
            {editing ? (
                <div>
                    <textarea
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        maxLength={2000}
                        rows={2}
                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                    />
                    <div className="mt-1 flex justify-end gap-1">
                        <button
                            onClick={handleCancel}
                            className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-gray-500 hover:bg-gray-100"
                        >
                            <X className="h-3 w-3" /> Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!editText.trim() || saving}
                            className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-0.5 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            <Check className="h-3 w-3" /> {saving ? '...' : 'Save'}
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{comment.CommentText}</p>
                        </div>
                        {canEdit && (
                            <div className="flex shrink-0 gap-0.5">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                                    className="rounded p-0.5 text-gray-400 hover:bg-blue-50 hover:text-blue-500"
                                    title="Edit comment"
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                                    title="Delete comment"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${typeStyle.bg} ${typeStyle.text}`}>
                            {typeStyle.label}
                        </span>
                        {comment.LotCode && (
                            <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                Lot: {comment.LotCode}
                            </span>
                        )}
                        <p className="text-xs text-gray-400">
                            {comment.CreatedBy} &middot; {new Date(comment.CreatedAt).toLocaleString('de-CH')}
                            {comment.UpdatedAt && (
                                <span className="ml-1 italic">(edited)</span>
                            )}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}

export function InventoryComments({ itemId, comments, canEdit, onCommentAdded, lotCode }: InventoryCommentsProps) {
    const [newComment, setNewComment] = useState('');
    const [commentType, setCommentType] = useState('note');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newComment.trim() || submitting) return;

        setSubmitting(true);
        try {
            await inventoryApi.addComment(itemId, newComment.trim(), commentType, lotCode ?? null);
            setNewComment('');
            setCommentType('note');
            onCommentAdded();
        } catch (err) {
            console.error('Failed to add comment:', err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                <span>Comments ({comments.length})</span>
                {lotCode && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                        New comments tagged Lot: {lotCode}
                    </span>
                )}
            </h4>

            {comments.length === 0 && (
                <p className="py-4 text-center text-xs text-gray-400">No comments yet.</p>
            )}

            <div className="max-h-80 space-y-2 overflow-y-auto">
                {comments.map(comment => (
                    <CommentItem
                        key={comment.Id}
                        comment={comment}
                        itemId={itemId}
                        canEdit={canEdit}
                        onChanged={onCommentAdded}
                    />
                ))}
            </div>

            {canEdit && (
                <form onSubmit={handleSubmit} className="mt-3">
                    <div className="flex gap-2">
                        <select
                            value={commentType}
                            onChange={e => setCommentType(e.target.value)}
                            className="h-8 rounded-md border border-gray-300 px-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="note">Note</option>
                            <option value="action">Action</option>
                            <option value="decision">Decision</option>
                        </select>
                        <input
                            type="text"
                            value={newComment}
                            onChange={e => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            maxLength={2000}
                            className="h-8 flex-1 rounded-md border border-gray-300 px-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                            type="submit"
                            disabled={!newComment.trim() || submitting}
                            className="inline-flex h-8 items-center gap-1 rounded-md bg-blue-600 px-3 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            <Send className="h-3 w-3" />
                            {submitting ? '...' : 'Send'}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
